// The raid team, drawn on the map tile it is standing on.
//
// A figure is a pixel silhouette about 15px tall in a tile's own 120px space —
// roughly a tenth of the tile, which is several times life size against the
// buildings beside it. That is deliberate: a to-scale person on this map would
// be two pixels and would vanish at the 64-80px the cell is actually displayed
// at. These are map TOKENS that happen to be shaped like people, and the thing
// that actually reads at that size is not the body at all, it is the torch —
// a moving light on dark ground is legible when the figure holding it is not.
//
// Drawn by raidMapAnim.js AFTER composite(), which means after the day tint's
// multiply pass. Nothing here is darkened for us, so everything here darkens
// itself (see shade()) — otherwise the team would glow at midnight like the
// lit windows do, and lit windows are lit for a reason.
import { daylightFraction } from './worldClock-core.js';
import { AVATAR_PALETTES } from './avatarGen.js';
import { memberPose } from './raidTeamAnim-core.js';
import { fxPhase, fxFade, fxTravel } from './raidFx-core.js';
import * as config from '../constants.js';

// Figure geometry, in the tile's authored 120px space. Scaled by u = tile/120
// at draw time so a different tile size costs nothing.
const REF_TILE = 120;
const BEAM_LEN = 30;        // how far the torch reaches across the ground
const BEAM_WIDE = 9;        // half-width where it lands
// The ground plane is seen from above, so a beam pointing up or down the
// screen is foreshortened. Squashing y by this much is the whole of the
// perspective in here, and it is enough to stop a torch aimed "north" from
// reading as one aimed at the sky.
const GROUND_SQUASH = 0.5;

// Torch strength against the hour. It never reaches zero: a team searching a
// tile at noon still has to LOOK like it is searching, and the faint daytime
// beam is the only thing on screen that says so. At night it carries the
// figure as well as the search.
const BEAM_DAY = 0.10, BEAM_NIGHT = 0.55;

// How dark a silhouette gets. Floored well above the tile's own night tint —
// a figure shaded honestly against midnight ground is a figure nobody can
// find, and these three are the thing the player is watching.
const SHADE_FLOOR = 0.52;

// ---- outcome effects ----
// A skill check resolving is the raid's whole beat and the map used to say
// nothing about it. These are one-shots played on the member the check ran
// through (see raidFx-core.js). All of them are drawn with 'lighter' and none
// of them is shaded for the hour: they are light sources, like the torches and
// the lit windows, and light is the one thing the day tint must not touch.
const FX = {
    // Chest height on the figure, where a person's hands are.
    HAND_Y: -10,
    success: { ring: 17, spokes: 0, color: '#b9f5c8', core: '#ffffff' },
    // Twice the reach and a full corona: a synergy is the rarest thing that
    // happens in a raid and it should be the one effect visible at a glance.
    synergy: { ring: 30, spokes: 7, color: '#ffd479', core: '#fff3cf' },
    failure: { ring: 15, spokes: 0, color: '#ff7a6b', core: '#ffd9d2' },
};
// How far a member is knocked about by a failure landing on them, in tile px.
const FX_JOLT = 2.2;

// A found item's mote is tinted by its rarity, off the same table the item
// tooltips and the inventory use — a rare coming up has to look like a rare,
// and inventing a second palette here would make it look like something else.
const RARITY_FALLBACK = '#cfd6e0';
function rarityColor(rarity) {
    return config.itemRarities?.[rarity]?.color || RARITY_FALLBACK;
}

const TAU = Math.PI * 2;
const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);

// Multiply a #rrggbb by a constant. Memoized because the same dozen
// (colour, brightness step) pairs recur every frame for the life of a raid.
const shadeCache = new Map();
function shade(hex, k) {
    const q = Math.round(k * 24);            // 24 brightness steps is under
    const key = `${hex}|${q}`;               // the eye's threshold at this size
    let out = shadeCache.get(key);
    if (out) return out;
    const n = parseInt(hex.slice(1), 16);
    const f = q / 24;
    out = `rgb(${Math.round(((n >> 16) & 255) * f)},${Math.round(((n >> 8) & 255) * f)},${Math.round((n & 255) * f)})`;
    shadeCache.set(key, out);
    return out;
}

/**
 * Normalise a raid roster into what the renderer needs. Called once per raid
 * tick rather than per frame — a member is a big object with skills, gear and
 * an inventory, and none of that draws anything.
 */
export function teamSprites(team) {
    return (team || [])
        .filter(m => m && m.isAlive !== false)
        .map((m, i) => ({
            // What an outcome effect addresses them by. The drawn roster drops
            // the dead, so an index would shift onto somebody else the moment
            // anyone fell — mid-raid, which is exactly when failures happen.
            id: m.id ?? m.avatarSeed ?? m.name ?? i,
            faction: AVATAR_PALETTES[m.faction] ? m.faction : 'dominion',
            // Decorrelates one member's wander and torch sweep from the next.
            // avatarSeed is the same token the portrait is generated from, so
            // a given contractor moves the same way every raid they are on.
            seed: hashSeed(m.avatarSeed || m.id || m.name || i),
            // Below a third HP the figure stoops and the torch dips — the map
            // says "this is going badly" before the log scrolls to it.
            hurt: m.maxHp > 0 ? clamp01(1 - (m.currentHp ?? m.maxHp) / m.maxHp) : 0,
        }));
}

function hashSeed(v) {
    const s = String(v);
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/**
 * Draw the team onto one tile's canvas.
 *
 * @param ctx     the tile canvas context — already composited for this frame
 * @param x,y     the party's centre in THIS tile's pixel space. May sit
 *                outside the canvas: a party straddling a seam is drawn on
 *                both tiles and each one clips its own half, which is how the
 *                walk across a tile boundary stays continuous without anyone
 *                owning a whole-map canvas.
 * @param opts.party    what partyAt() returned
 * @param opts.members  what teamSprites() returned
 * @param opts.tSec     animation clock, seconds
 * @param opts.dayPhase 0..1 local day phase at the raid site
 * @param opts.tile     tile canvas size in px
 * @param opts.posts    searchPosts() for the tile the team is STANDING on —
 *                      the same list for every canvas this frame, since the
 *                      party has one set of posts however many tiles it is
 *                      drawn across
 * @param opts.resting  a campsite: they sit down round the fire and put their
 *                      torches out rather than working the ground
 * @param opts.fx       one-shot outcome effects still running, from
 *                      raidFx-core; each names the member it plays on
 * @param opts.nowMs    wall clock the effects are timed against — they are
 *                      posted by the raid loop, which runs on Date.now()
 */
export function drawTeam(ctx, x, y, opts) {
    const members = opts.members;
    if (!members || !members.length) return;
    const tile = opts.tile || REF_TILE;
    const u = tile / REF_TILE;
    const tSec = opts.tSec || 0;
    const light = daylightFraction(opts.dayPhase ?? 0.5);
    const beamA = BEAM_DAY + (BEAM_NIGHT - BEAM_DAY) * (1 - light);
    const bright = SHADE_FLOOR + (1 - SHADE_FLOOR) * light;
    const nowMs = opts.nowMs ?? Date.now();

    // Effects are addressed by member id, because the drawn roster drops the
    // dead and an index would quietly shift onto somebody else the moment
    // anyone fell. One that names nobody on screen plays on the whole party.
    const fx = [];
    for (const f of opts.fx || []) {
        const phase = fxPhase(f, nowMs);
        if (phase <= 0 || phase >= 1) continue;      // not started, or over
        fx.push({ f, phase, i: members.findIndex(m => m.id != null && m.id === f.memberId) });
    }

    // A party near a corner is inside the draw radius of all eight neighbouring
    // tiles, but a member only ever puts ink within a torch's length of itself.
    // Dropping the ones that cannot reach this canvas is what stops a stationary
    // team costing three beam gradients on each of nine tiles every frame.
    // An effect reaches further than a torch, so the margin grows while one runs.
    const reach = (fx.length ? FX.synergy.ring * 1.3 : BEAM_LEN * 1.15) * u;
    const poses = [];
    for (let i = 0; i < members.length; i++) {
        const p = memberPose(i, members.length, tSec, opts.party, members[i].seed, opts.posts?.[i]);
        let px = x + p.dx * tile, py = y + p.dy * tile;
        if (px < -reach || px > tile + reach || py < -reach || py > tile + reach) continue;
        // A failure landing on somebody knocks them about — the one place an
        // effect moves a figure rather than being drawn beside it, because a
        // flinch is what tells you WHO it happened to.
        let jolt = 0;
        for (const e of fx) {
            if (e.f.kind !== 'failure' || e.i !== i) continue;
            jolt += Math.sin(e.phase * 34) * FX_JOLT * u * fxFade(e.phase);
        }
        px += jolt;
        // How far down they have sat. `gait` is already the "have they settled
        // onto their post yet" number, so a camp needs no second clock: they
        // walk in, set up, and sink down as they arrive, then stand back up as
        // the party gathers to leave.
        poses.push({ m: members[i], p, px, py, i, sit: opts.resting ? 1 - p.gait : 0 });
    }
    if (!poses.length) return;
    // Painter's order: whoever is further up the tile is further away.
    poses.sort((a, b) => a.py - b.py);

    // Every torch first, so a beam never lies over the figure standing in it.
    for (const s of poses) drawBeam(ctx, s, u, beamA, tSec);
    for (const s of poses) drawFigure(ctx, s, u, bright, beamA);
    // ...and the outcome over everything, because it is the news.
    for (const e of fx) drawFx(ctx, e, poses, x, y, u);
}

function drawBeam(ctx, s, u, alpha, tSec) {
    // A wounded member's torch hangs low and short, and a seated one has put
    // theirs down — a camp is lit by its fire, which the tile draws itself.
    const a = alpha * (1 - 0.45 * s.m.hurt) * (1 - s.sit);
    if (a < 0.02) return;
    const len = BEAM_LEN * u * (1 - 0.25 * s.m.hurt);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.translate(s.px, s.py);
    ctx.scale(1, GROUND_SQUASH);
    ctx.rotate(s.p.facing);
    const g = ctx.createLinearGradient(0, 0, len, 0);
    g.addColorStop(0, `rgba(255,246,214,${a})`);
    g.addColorStop(0.4, `rgba(255,238,196,${a * 0.5})`);
    g.addColorStop(1, 'rgba(255,232,176,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(len, -BEAM_WIDE * u);
    ctx.lineTo(len * 1.06, 0);
    ctx.lineTo(len, BEAM_WIDE * u);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // The pool of light around their own feet, which is what actually lifts
    // the figure off dark ground. Breathes slightly, so a stationary team
    // still has something moving about it.
    const r = 7 * u * (1 + 0.06 * Math.sin(tSec * 1.7 + s.m.seed % 7));
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.translate(s.px, s.py);
    ctx.scale(1, GROUND_SQUASH);
    const pool = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    pool.addColorStop(0, `rgba(255,240,205,${a * 0.55})`);
    pool.addColorStop(1, 'rgba(255,230,180,0)');
    ctx.fillStyle = pool;
    ctx.fillRect(-r, -r, r * 2, r * 2);
    ctx.restore();
}

function drawFigure(ctx, s, u, bright, beamA) {
    const pal = AVATAR_PALETTES[s.m.faction];
    const garb = shade(pal.garb, bright);
    const dark = shade(pal.garbDark, bright);
    const accent = shade(pal.accent, bright * 0.9);
    const px = s.px, py = s.py;
    // Which way the sprite is turned. The body is a flat upright token on a
    // top-down map, so only the sign of the torch's x-component is expressible
    // — the true direction lives in the beam.
    const face = Math.cos(s.p.facing) >= 0 ? 1 : -1;
    // Nobody strides while sitting down.
    const swing = s.p.stride * (1 - s.sit);
    // Wounded members stoop; a full-height stride also lifts the body a little
    // off its feet on each step. Sitting takes a third off the whole figure,
    // which at fifteen pixels is the difference between standing and not.
    const H = (1 - 0.13 * s.m.hurt) * (1 - 0.34 * s.sit);
    const bob = -Math.abs(swing) * 0.7 * u * s.p.gait;

    const r = (dx, dy, w, h, c) => {
        ctx.fillStyle = c;
        ctx.fillRect(Math.round(px + dx * u), Math.round(py + dy * u * H + bob),
            Math.max(1, Math.round(w * u)), Math.max(1, Math.round(h * u)));
    };

    // contact shadow — the only thing tying a token to the ground
    ctx.save();
    ctx.globalAlpha = 0.34;
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.ellipse(px, py, 4.4 * u, 1.7 * u, 0, 0, TAU);
    ctx.fill();
    ctx.restore();

    // A dark ground line under the whole figure. At noon the silhouette sits
    // on pale concrete with nothing separating the two, and one row of shadow
    // is cheaper than outlining every part.
    r(-3.2, -0.8, 6.4, 1, '#000000');

    // Legs, swinging out of phase — and drawn out in front rather than under
    // the body once they are sitting, which is the read at this size: knees up,
    // not a shorter person.
    if (s.sit > 0.5) {
        r(-1 + face * 1.2, -2.6, 3.4, 2.6, dark);
    } else {
        r(-2.4 + swing * 1.1, -5, 2, 5, dark);
        r(0.4 - swing * 1.1, -5, 2, 5, dark);
    }
    // pack, on the back — behind whichever way they are turned
    r(face > 0 ? -3.9 : 1.9, -10.5, 2, 4.5, dark);
    // torso and shoulders
    r(-3, -11, 6, 6.2, garb);
    r(-3.4, -11.6, 6.8, 1.6, dark);
    // a single accent pixel each, so three silhouettes are three people
    r(face > 0 ? 1.4 : -2.4, -10.6, 1, 1, accent);
    // head — one row narrower top and bottom, which is all a 4px head needs
    // to stop reading as a brick
    r(-2.1, -15.6, 4.2, 3.8, dark);
    r(-1.4, -16.1, 2.8, 0.6, dark);

    // Underlight from their own torch, on the side they are holding it. WARM
    // and only a few pixels: the earlier version ran a cold rim down the full
    // height of the body, which at this size is not a highlight, it is a
    // fluorescent tube standing next to a person. What actually separates a
    // figure from dark ground is the pool it is standing in, not its edge.
    ctx.save();
    ctx.globalAlpha = Math.min(0.45, 0.12 + beamA * 0.5);
    const lit = face > 0 ? 2.2 : -2.8;
    r(lit, -11.2, 0.6, 2.4, '#ffe9bd');   // shoulder catching the lamp
    r(lit - face * 0.2, -15.2, 0.6, 1.4, '#ffe4b0');   // cheekbone
    ctx.restore();
}

// ============================================================================
// OUTCOME EFFECTS
// ============================================================================
// Everything here is drawn with 'lighter' onto the composited tile, so it adds
// light rather than painting over the art. That is also why none of it is put
// through shade(): a flare is a light source, and the day tint has already
// been applied underneath it.

/**
 * Where an effect happens.
 *
 * On its member's hands. An effect that names a member who is not drawn on
 * THIS canvas returns null and is skipped here — they are drawn on a
 * neighbouring tile and their effect belongs over there with them. Falling
 * back to the party's centre instead would put a second copy of every flare
 * in the wrong place on every tile the member had been culled from.
 *
 * The centre is only for an effect naming nobody on the roster at all — a
 * member who fell between the check resolving and the frame being drawn.
 */
function fxOrigin(e, poses, x, y, u) {
    if (e.i >= 0) {
        const on = poses.find(p => p.i === e.i);
        return on ? { x: on.px, y: on.py + FX.HAND_Y * u } : null;
    }
    return { x, y: y + FX.HAND_Y * u };
}

function drawFx(ctx, e, poses, x, y, u) {
    const at = fxOrigin(e, poses, x, y, u);
    if (!at) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    if (e.f.kind === 'loot') drawLootMote(ctx, e, at, u);
    else drawBurst(ctx, e, at, u);
    ctx.restore();
}

/**
 * Success, synergy and failure: a ring going out from the member's hands, a
 * core that flashes and dies, and — for a synergy only — a corona of spokes.
 *
 * One routine for the three of them because they differ in colour, reach and
 * whether they have spokes, and in nothing else. Three near-identical draw
 * functions would drift apart the first time one was tuned.
 */
function drawBurst(ctx, e, at, u) {
    const spec = FX[e.f.kind] || FX.success;
    const p = e.phase;
    const fade = fxFade(p);
    const r = Math.max(1, fxTravel(p) * spec.ring * u);

    // the ring
    ctx.globalAlpha = Math.min(1, fade * 0.95);
    ctx.strokeStyle = spec.color;
    ctx.lineWidth = Math.max(1, (1 + 2.2 * (1 - p)) * u);
    ctx.beginPath();
    // Squashed on the same ground plane the torches use, so a ring reads as
    // lying on the tile rather than standing up off it.
    ctx.ellipse(at.x, at.y, r, r * GROUND_SQUASH, 0, 0, TAU);
    ctx.stroke();

    // the core: a hard flash at the hands that is gone within a third of the
    // effect, which is what gives it a moment of impact rather than a swell
    const coreP = clamp01(p / 0.33);
    if (coreP < 1) {
        const cr = (2 + 5 * coreP) * u;
        const g = ctx.createRadialGradient(at.x, at.y, 0, at.x, at.y, cr);
        g.addColorStop(0, spec.core);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalAlpha = fxFade(coreP);
        ctx.fillStyle = g;
        ctx.fillRect(at.x - cr, at.y - cr, cr * 2, cr * 2);
    }

    // the corona — synergy only. Two skills meeting is the fiction, so the
    // spokes come in pairs about a common centre rather than radiating evenly.
    if (spec.spokes) {
        ctx.globalAlpha = fade * 0.8;
        ctx.strokeStyle = spec.core;
        ctx.lineWidth = Math.max(1, 1.2 * u);
        for (let i = 0; i < spec.spokes; i++) {
            const a = (i / spec.spokes) * TAU + p * 0.7 + (e.f.seed % 100) * 0.06;
            const inner = r * 0.55, outer = r * (1 + 0.35 * Math.sin(i * 2.3));
            ctx.beginPath();
            ctx.moveTo(at.x + Math.cos(a) * inner, at.y + Math.sin(a) * inner * GROUND_SQUASH);
            ctx.lineTo(at.x + Math.cos(a) * outer, at.y + Math.sin(a) * outer * GROUND_SQUASH);
            ctx.stroke();
        }
    }
}

/**
 * A found item: a mote that lifts out of the member's hands, hangs, and goes
 * out — tinted by the item's rarity, which is the whole reason for putting it
 * on the map. A common coming up should look like nothing much; a rare should
 * make you look.
 */
function drawLootMote(ctx, e, at, u) {
    const p = e.phase;
    const color = rarityColor(e.f.rarity);
    // Rises most of the way in the first half, then holds and fades, so the
    // eye has something still to land on rather than chasing it off the tile.
    const rise = fxTravel(clamp01(p / 0.55)) * 11 * u;
    const drift = Math.sin((e.f.seed % 20) + p * 2.2) * 2.4 * u;
    const cx = at.x + drift, cy = at.y - rise;
    const fade = p < 0.15 ? p / 0.15 : fxFade((p - 0.15) / 0.85);

    // halo
    const hr = 6 * u;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, hr);
    g.addColorStop(0, color);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalAlpha = fade * 0.75;
    ctx.fillStyle = g;
    ctx.fillRect(cx - hr, cy - hr, hr * 2, hr * 2);

    // the mote itself, and a four-point sparkle as it peaks
    ctx.globalAlpha = Math.min(1, fade * 1.2);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(Math.round(cx - u), Math.round(cy - u), Math.max(1, 2 * u), Math.max(1, 2 * u));
    const spark = 1 - Math.abs(p - 0.5) * 3.2;
    if (spark > 0) {
        ctx.globalAlpha = spark * 0.9;
        ctx.fillStyle = color;
        const arm = 4 * u * spark;
        ctx.fillRect(cx - arm, cy, arm * 2, Math.max(1, u));
        ctx.fillRect(cx, cy - arm, Math.max(1, u), arm * 2);
    }
}
